/**
 * Feb 2026 · Sales Dashboard.
 *
 * Executive view for the Sales team — booking momentum, financial value,
 * dispatch backlog, on-time %, performance curve, market split and top
 * customer rankings. Every panel is refreshable via a single
 * GET /api/dashboard/sales call (server-side aggregation over
 * sales_orders + export_orders + production_plans + production_entries +
 * delivery_challans).
 */
import { useIsMobile } from "@/lib/mobile.jsx";
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { MonthYearPicker } from "@/components/MonthYearPicker";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  ClockIcon, PackageIcon, CurrencyInrIcon, ChartBarIcon, TrendUpIcon, GaugeIcon,
  UsersThreeIcon, GlobeIcon, StorefrontIcon, HourglassIcon, ArrowsCounterClockwiseIcon,
  CaretUpIcon, CaretDownIcon, MinusIcon,
} from "@phosphor-icons/react";

// ---------- format helpers ----------
// Feb 2026 · Everything on the Sales Dashboard renders in Indian **Lakhs**
// (1 L = 1,00,000) — meters and INR alike. Ops team asked for a single
// mental unit across the whole dashboard so cross-comparisons are
// instantaneous. Values < 1 L show two decimals (0.45 L), values ≥ 1 L
// show one decimal (12.5 L). No auto-switch to Cr — leadership prefers
// counting Lakhs consistently.
const nf = new Intl.NumberFormat("en-IN");
const _lakh = (v) => {
  const n = Number(v || 0);
  const l = n / 1e5;
  if (Math.abs(l) >= 100) return l.toFixed(0);
  if (Math.abs(l) >= 1)   return l.toFixed(1);
  return l.toFixed(2);
};
const fmtM = (v) => `${_lakh(v)} L`;                 // meters → Lakhs (unit "m" is rendered separately as "L m")
const fmtInr = (v) => `₹${_lakh(v)} L`;               // INR totals → Lakhs
const fmtInrFull = (v) => `₹${_lakh(v)} L`;           // Tooltip · same Lakhs display
// Feb 2026 · Per-meter Rate stays in full rupees — turning ₹16 into "0.00 L"
// hides real ASP movement from planners. Keeps 2-dp for < 100 and rounds
// beyond so the tile doesn't churn on decimals for coarse products.
const fmtRate = (v) => {
  const n = Number(v || 0);
  if (!n) return "₹0";
  return n >= 100 ? `₹${nf.format(Math.round(n))}` : `₹${n.toFixed(2)}`;
};
const fmtPct = (v) => `${(v ?? 0).toFixed(1)}%`;
const shortDate = (d) => (d || "").slice(5); // MM-DD

// Palette (kept in sync with dashboard theme)
const CHART_COLORS = {
  booked: "var(--accent)",
  produced: "#10b981",
  dispatched: "#f59e0b",
};
const MARKET_COLORS = ["var(--accent)", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6"];

// ---------- Reusable cards ----------
const KPI = ({ label, value, unit, sub, color = "text-white", icon: Icon, testId }) => (
  <div className="tactical-card p-4" data-testid={testId}>
    <div className="flex items-start justify-between mb-2">
      <div className="overline">{label}</div>
      {Icon && (
        <div className="w-7 h-7 rounded-sm bg-[var(--bg-2)] flex items-center justify-center">
          <Icon size={13} weight="bold" className={color} />
        </div>
      )}
    </div>
    <div className={`font-mono text-2xl font-semibold ${color}`}>
      {value}
      {unit && <span className="text-xs text-zinc-500 ml-1">{unit}</span>}
    </div>
    {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
  </div>
);

const MiniStat = ({ label, value, unit, color = "text-white", testId }) => (
  <div className="rounded-sm border border-[var(--border)] p-3 bg-[var(--bg-2)]/40" data-testid={testId}>
    <div className="overline text-[9px] mb-1">{label}</div>
    <div className={`font-mono text-xl font-semibold ${color}`}>
      {value}
      {unit && <span className="text-[10px] text-zinc-500 ml-1">{unit}</span>}
    </div>
  </div>
);

// On-Time gauge — semi-donut in Recharts + centered %.
// Feb 2026 · Compact percent-chip variant renders on mobile so 4G paint
// stays under 2 s (recharts import weight avoided for the mobile bundle).
function OnTimeGauge({ pct, onTime, late }) {
  const pctSafe = Math.max(0, Math.min(100, pct || 0));
  const color = pctSafe >= 90 ? "#10b981" : pctSafe >= 70 ? "#f59e0b" : "#ef4444";
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <div className="tactical-card p-4 flex flex-col" data-testid="sales-kpi-on-time-gauge">
        <div className="flex items-start justify-between mb-2">
          <div className="overline">On-Time Delivery</div>
          <div className="w-7 h-7 rounded-sm bg-[var(--bg-2)] flex items-center justify-center">
            <GaugeIcon size={13} weight="bold" style={{ color }} />
          </div>
        </div>
        <div className="flex items-baseline gap-2 mt-1" data-testid="sales-on-time-chip">
          <div className="font-mono text-3xl font-bold tabular-nums" style={{ color }}>{fmtPct(pctSafe)}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{onTime}/{onTime + late} on-time</div>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-[var(--bg-2)] overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pctSafe}%`, backgroundColor: color }} />
        </div>
        <div className="text-[10px] text-zinc-500 mt-1.5">{late} delivery{late === 1 ? "" : "ies"} late</div>
      </div>
    );
  }
  const data = [
    { name: "On-time", value: pctSafe, color },
    { name: "Remaining", value: 100 - pctSafe, color: "rgba(255,255,255,0.06)" },
  ];
  return (
    <div className="tactical-card p-4 flex flex-col" data-testid="sales-kpi-on-time-gauge">
      <div className="flex items-start justify-between mb-2">
        <div className="overline">On-Time Delivery</div>
        <div className="w-7 h-7 rounded-sm bg-[var(--bg-2)] flex items-center justify-center">
          <GaugeIcon size={13} weight="bold" style={{ color }} />
        </div>
      </div>
      <div className="relative flex-1" style={{ minHeight: 110 }}>
        <ResponsiveContainer width="100%" height={130}>
          <PieChart>
            <Pie
              data={data}
              startAngle={180}
              endAngle={0}
              innerRadius="70%"
              outerRadius="100%"
              paddingAngle={0}
              dataKey="value"
              stroke="none"
              isAnimationActive={false}
            >
              {data.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-x-0 top-[55%] text-center">
          <div className="font-mono text-3xl font-bold" style={{ color }}>{fmtPct(pctSafe)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{onTime} on-time · {late} late</div>
        </div>
      </div>
    </div>
  );
}

// ---------- Main page ----------
// Feb 2026 · Executive-rhythm views — the toggle now scans across Total /
// Today / Yesterday / This Week / MTD / Last Month / any-day. Each view
// swaps the hero grid to the matching snapshot payload while the Delta
// card holds a fixed Today-vs-Yesterday reading so momentum stays
// glance-visible on every screen.
const VIEW_ACCENT = {
  total:     "text-[var(--accent)]",
  today:     "text-[var(--accent)]",
  yesterday: "text-amber-300",
  week:      "text-cyan-300",
  mtd:       "text-indigo-300",
  lastmonth: "text-fuchsia-300",
  month:     "text-orange-300",
  custom:    "text-teal-300",
};
// Each view → its backend snapshot. Total View = FYTD (1 Apr → today); month = picked YYYY-MM.
const SNAPSHOT_KEY = {
  total: "fytd_snapshot", today: "today_snapshot", yesterday: "yesterday_snapshot", week: "week_snapshot",
  mtd: "mtd_snapshot", lastmonth: "last_month_snapshot", month: "selected_month_snapshot", custom: "custom_snapshot",
};
const thisMonth = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 7);

// Compact ▲ / ▼ / — chip for the Today-vs-Yesterday momentum card.
function DeltaChip({ today, yesterday, format, unit, label, testId }) {
  const t = Number(today || 0);
  const y = Number(yesterday || 0);
  const diff = t - y;
  const pct = y > 0 ? (diff / y) * 100 : (t > 0 ? 100 : 0);
  const up = diff > 0.0001;
  const down = diff < -0.0001;
  const color = up ? "text-emerald-400" : down ? "text-red-400" : "text-zinc-400";
  const Icon = up ? CaretUpIcon : down ? CaretDownIcon : MinusIcon;
  return (
    <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-2)]/40 p-3" data-testid={testId}>
      <div className="overline text-[9px] mb-1 text-zinc-500">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="font-mono text-lg text-white tabular-nums">{format(t)}{unit && <span className="text-[10px] text-zinc-500 ml-1">{unit}</span>}</div>
        <div className={`flex items-center gap-0.5 text-xs font-mono ${color}`}>
          <Icon size={11} weight="bold" />
          {y > 0 ? `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%` : (t > 0 ? "NEW" : "—")}
        </div>
      </div>
      <div className="text-[10px] text-zinc-500 mt-1">Yesterday · {format(y)}{unit ? ` ${unit}` : ""}</div>
    </div>
  );
}

// Snapshot-driven 6-KPI hero grid used by every period-based view. Keeps
// the markup DRY so today / yesterday / week / mtd / last-month / custom
// all share the exact same card layout, spacing, and colour language.
function PeriodHero({ snapshot, periodLabel, accent = "text-[var(--accent)]", testId }) {
  const s = snapshot || {};
  const priceBooked = (s.booked_meters || 0) > 0 ? (s.booked_value_inr || 0) / s.booked_meters : 0;
  const priceSales  = (s.sales_meters  || 0) > 0 ? (s.sales_value_inr  || 0) / s.sales_meters  : 0;
  const Tile = ({ label, value, sub, color, tid }) => (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-1)] shadow-xl shadow-black/40 p-5 text-center" data-testid={tid}>
      <div className="overline text-[10px] text-zinc-400">{label}</div>
      <div className={`font-display font-black text-3xl sm:text-4xl mt-2 tabular-nums ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{sub}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5" data-testid={testId}>
      <Tile label="Order Booked" value={fmtM(s.booked_meters)}       sub={`Meters · ${periodLabel}`}       color={accent} tid="period-booked-m" />
      <Tile label="Order Booked" value={fmtInr(s.booked_value_inr)}  sub={`Value · ${periodLabel}`}        color={accent} tid="period-booked-v" />
      <Tile label="Order Booked" value={fmtRate(priceBooked)}         sub={`Price Avg / m · ${periodLabel}`} color={accent} tid="period-booked-avg" />
      <Tile label="Sales (Delivered)" value={fmtM(s.sales_meters)}       sub={`Meters · ${periodLabel}`}       color="text-emerald-400" tid="period-sales-m" />
      <Tile label="Sales (Delivered)" value={fmtInr(s.sales_value_inr)}  sub={`Value · ${periodLabel}`}        color="text-emerald-400" tid="period-sales-v" />
      <Tile label="Sales (Delivered)" value={fmtRate(priceSales)}         sub={`Price Avg / m · ${periodLabel}`} color="text-emerald-400" tid="period-sales-avg" />
    </div>
  );
}

// ---------- Main page ----------
export default function SalesDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("total"); // "total" | "today" | "yesterday" | "week" | "mtd" | "lastmonth" | "custom"
  const [customDate, setCustomDate] = useState("");
  const [month, setMonth] = useState("");

  const load = async () => {
    try {
      const params = { period: view };
      if (customDate) params.date = customDate;
      if (view === "month" && month) params.month = month;
      const { data } = await api.get(`/dashboard/sales`, { params });
      setData(data);
    } catch (e) { console.warn("sales dashboard load failed", e); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customDate, view, month]);

  const perfSeries = useMemo(() => (data?.performance_series || []).map(d => ({ ...d, date: d.label || shortDate(d.date) })), [data]);
  const marketPie = useMemo(() => data ? [
    { name: "Domestic", value: data.market_split?.domestic_value_inr || 0 },
    { name: "Export", value: data.market_split?.export_value_inr || 0 },
  ].filter(r => r.value > 0) : [], [data]);

  if (loading && !data) {
    return <div className="p-6 text-center text-zinc-500" data-testid="sales-dashboard-loading">Loading Sales Dashboard…</div>;
  }
  if (!data) {
    return <div className="p-6 text-center text-zinc-500">Failed to load — try refreshing.</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0" data-testid="sales-dashboard">
      {/* Header · Feb 2026 · Executive-style centered title (Power BI-style layout) */}
      <div className="text-center pt-2 pb-6 border-b border-[var(--border)]">
        <div className="overline text-[10px] tracking-[0.3em] text-zinc-500">NARROW FABRIC · REAL-TIME KPIs</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tight text-white mt-2">Executive Sales Overview</h1>
        <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
          <div className="inline-flex rounded-md border border-[var(--border)] overflow-hidden text-xs font-mono" data-testid="sales-view-toggle">
            {[
              { k: "total",     label: "Total View" },
              { k: "today",     label: "Today" },
              { k: "yesterday", label: "Yesterday" },
              { k: "week",      label: "This Week" },
              { k: "mtd",       label: "MTD" },
              { k: "lastmonth", label: "Last Month" },
            ].map((b, i) => (
              <button
                key={b.k}
                onClick={() => { setView(b.k); setCustomDate(""); setMonth(""); }}
                data-testid={`sales-view-${b.k}`}
                className={`h-10 sm:h-9 px-3 uppercase tracking-widest ${i > 0 ? "border-l border-[var(--border)]" : ""} ${view === b.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
              >{b.label}</button>
            ))}
          </div>
          {/* Month picker · any month/year → KPIs + charts scoped to that whole month. */}
          <MonthYearPicker
            value={month}
            max={thisMonth()}
            active={view === "month"}
            onChange={v => { setMonth(v); setCustomDate(""); setView("month"); }}
            testId="sales-view-month-picker"
          />
          {/* Custom-day picker · picking any date snaps the hero grid to
              that single day so sales can drill into a specific
              historical shift without the preset buttons. */}
          <input
            type="date"
            value={customDate}
            onChange={e => { setCustomDate(e.target.value); setMonth(""); setView(e.target.value ? "custom" : "total"); }}
            className="h-10 sm:h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-md text-xs text-white focus:outline-none focus:border-[var(--accent)]"
            data-testid="sales-view-custom-date"
            title="Any single day"
          />
          <button onClick={load} className="h-10 sm:h-9 px-3 rounded-md border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs flex items-center gap-1.5" data-testid="sales-dashboard-refresh">
            <ArrowsCounterClockwiseIcon size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Feb 2026 · Today vs Yesterday momentum card — pinned above the
          hero grid so leadership always sees the day-over-day delta,
          even when they've tabbed to a longer-range view. */}
      <div className="tactical-card p-4 sm:p-5" data-testid="sales-delta-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Momentum</div>
            <div className="font-display text-base sm:text-lg font-bold text-white">Today vs Yesterday</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Δ % day-over-day</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <DeltaChip label="Booked · Meters" today={data.today_snapshot?.booked_meters}     yesterday={data.yesterday_snapshot?.booked_meters}     format={fmtM}   unit="m" testId="delta-booked-m" />
          <DeltaChip label="Booked · Value"  today={data.today_snapshot?.booked_value_inr}  yesterday={data.yesterday_snapshot?.booked_value_inr}  format={fmtInr}          testId="delta-booked-v" />
          <DeltaChip label="Sales · Meters"  today={data.today_snapshot?.sales_meters}      yesterday={data.yesterday_snapshot?.sales_meters}      format={fmtM}   unit="m" testId="delta-sales-m" />
          <DeltaChip label="Sales · Value"   today={data.today_snapshot?.sales_value_inr}   yesterday={data.yesterday_snapshot?.sales_value_inr}   format={fmtInr}          testId="delta-sales-v" />
        </div>
      </div>

      {/* ─────────── HERO KPI ROW · snapshot-driven for every period ─────────── */}
      <PeriodHero
        snapshot={data[SNAPSHOT_KEY[view]]}
        periodLabel={
          view === "total"     ? "FYTD"                                       :
          view === "today"     ? "Today"                                      :
          view === "yesterday" ? `Yesterday · ${data.yesterday_snapshot?.date || ""}` :
          view === "week"      ? "This Week"                                  :
          view === "mtd"       ? "Month-to-Date"                              :
          view === "lastmonth" ? `Last Month · ${(data.last_month_snapshot?.start || "").slice(0,7)}` :
          view === "month"     ? (data.window?.label || `Month · ${month}`)   :
          `Custom · ${customDate}`
        }
        accent={VIEW_ACCENT[view] || "text-[var(--accent)]"}
        testId={`sales-section-${view}-hero`}
      />

      {/* ─────────── SECTION · SECONDARY OPS METRICS ─────────── */}
      <section data-testid="sales-section-operations" className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-sky-400 rounded-sm" />
          <h2 className="font-display text-sm font-semibold uppercase tracking-widest text-zinc-300">Operations Detail</h2>
          <span className="text-[10px] text-zinc-500 ml-2">Pipeline · packing rhythm · backlog value</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-1)] shadow-lg shadow-black/40 p-5" data-testid="sales-tile-status">
            <div className="flex items-start justify-between mb-4">
              <div className="overline">Pipeline (Meters)</div>
              <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center">
                <HourglassIcon size={14} weight="bold" className="text-amber-400" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <MiniStat label="Prod." value={fmtM(data.status?.pending_production_m)} unit="m" color="text-blue-300" testId="sales-status-production" />
              <MiniStat label="Dispatch" value={fmtM(data.status?.pending_dispatch_m)} unit="m" color="text-amber-300" testId="sales-status-dispatch" />
              <MiniStat label="Backlog" value={fmtM(data.status?.backlog_meters)} unit="m" color="text-red-300" testId="sales-status-backlog" />
            </div>
          </div>
          <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-1)] shadow-lg shadow-black/40 p-5" data-testid="sales-tile-packed-production">
            <div className="flex items-start justify-between mb-4">
              <div className="overline">Packed Production</div>
              <div className="w-8 h-8 rounded-full bg-sky-500/15 flex items-center justify-center">
                <PackageIcon size={14} weight="bold" className="text-sky-400" />
              </div>
            </div>
            <div className="grid gap-3 grid-cols-2">
              {view === "yesterday" ? (<>
                <MiniStat label="Yesterday · Meters" value={fmtM(data.packing_slips?.yesterday_meters)} unit="m" color="text-sky-300" testId="sales-packed-yesterday-m" />
                <MiniStat label="Yesterday · Value" value={fmtInr((data.packing_slips?.yesterday_meters || 0) * (data.yesterday_snapshot?.asp_per_m || data.financials?.avg_selling_price_inr || 0))} color="text-sky-300" testId="sales-packed-yesterday-v" />
              </>) : view === "today" ? (<>
                <MiniStat label="Today · Meters" value={fmtM(data.packing_slips?.today_meters)} unit="m" color="text-sky-300" testId="sales-packed-today-m" />
                <MiniStat label="Today · Value" value={fmtInr((data.packing_slips?.today_meters || 0) * (data.today_snapshot?.asp_per_m || data.financials?.avg_selling_price_inr || 0))} color="text-sky-300" testId="sales-packed-today-v" />
              </>) : (() => {
                // Every other view reads packed meters off its own snapshot
                // and derives value via that snapshot's ASP (parity with the hero grid).
                const snap = data[SNAPSHOT_KEY[view]];
                const lbl = { total: "FYTD", week: "Week", mtd: "MTD", lastmonth: "Last Month", month: "Month" }[view] || "Custom";
                const asp = (snap?.asp_per_m || data.financials?.avg_selling_price_inr || 0);
                return (<>
                  <MiniStat label={`${lbl} · Meters`} value={fmtM(snap?.packed_meters)} unit="m" color="text-sky-300" testId={`sales-packed-${view}-m`} />
                  <MiniStat label={`${lbl} · Value`} value={fmtInr((snap?.packed_meters || 0) * asp)} color="text-sky-300" testId={`sales-packed-${view}-v`} />
                </>);
              })()}
            </div>
          </div>
          <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-1)] shadow-lg shadow-black/40 p-5" data-testid="sales-tile-backlog-value">
            <div className="flex items-start justify-between mb-4">
              <div className="overline">Backlog Value</div>
              <div className="w-8 h-8 rounded-full bg-red-500/15 flex items-center justify-center">
                <CurrencyInrIcon size={14} weight="bold" className="text-red-400" />
              </div>
            </div>
            <div className="text-center">
              <div className="font-display font-black text-3xl text-amber-400 tabular-nums mt-1" data-testid="sales-backlog-value">{fmtInr(data.financials?.backlog_value_inr)}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Open Orders · unshipped</div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------- MIDDLE ROW ------------- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Performance chart */}
        <div className="tactical-card p-5 lg:col-span-2" data-testid="sales-chart-performance">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="overline" data-testid="sales-chart-window">Performance · {data.window?.label || "14 days"}</div>
              <div className="font-display text-lg font-bold text-white">Booked vs Produced vs Dispatched</div>
            </div>
            <ChartBarIcon size={20} className="text-[var(--accent)]" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={perfSeries}>
              <CartesianGrid stroke="#27272A" strokeDasharray="2 6" vertical={false} opacity={0.35} />
              <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => `${(Number(v || 0) / 1e5).toFixed(1)}L`} />
              <Tooltip
                contentStyle={{ background: "#18181B", border: "1px solid #27272A", fontSize: 12 }}
                formatter={(v) => `${_lakh(v)} L m`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="booked" name="Booked" fill={CHART_COLORS.booked} radius={[2, 2, 0, 0]} />
              <Bar dataKey="produced" name="Produced" fill={CHART_COLORS.produced} radius={[2, 2, 0, 0]} />
              <Bar dataKey="dispatched" name="Dispatched" fill={CHART_COLORS.dispatched} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Market split */}
        <div className="tactical-card p-5" data-testid="sales-chart-market">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="overline">Market Split · {data.window?.label || ""}</div>
              <div className="font-display text-lg font-bold text-white">Export vs Domestic</div>
            </div>
            <GlobeIcon size={20} className="text-[var(--accent)]" />
          </div>
          {marketPie.length ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={marketPie} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {marketPie.map((_, i) => (<Cell key={i} fill={MARKET_COLORS[i % MARKET_COLORS.length]} />))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#18181B", border: "1px solid #27272A", fontSize: 12 }}
                    formatter={(v) => fmtInrFull(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 pt-3 border-t border-[var(--border)] overflow-x-auto">
                <div className="overline text-[9px] mb-2">Regional breakdown</div>
                <table className="w-full text-xs min-w-[220px]">
                  <thead>
                    <tr className="text-zinc-500 text-[10px] uppercase tracking-widest">
                      <th className="text-left py-1">Region</th>
                      <th className="text-right py-1">Value</th>
                      <th className="text-right py-1 w-14">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.market_split?.regions || []).slice(0, 6).map(r => (
                      <tr key={r.region} className="border-t border-[var(--border)]/50" data-testid={`sales-region-${r.region}`}>
                        <td className="py-1 text-white">{r.region}</td>
                        <td className="py-1 text-right font-mono text-zinc-300">{fmtInr(r.value_inr)}</td>
                        <td className="py-1 text-right font-mono text-zinc-500">{r.share_pct}%</td>
                      </tr>
                    ))}
                    {(data.market_split?.regions || []).length === 0 && (
                      <tr><td colSpan={3} className="py-3 text-center text-zinc-500">No region data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center text-zinc-500 py-8 text-sm">No market data yet</div>
          )}
        </div>
      </div>

      {/* Product analysis */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="tactical-card p-5 lg:col-span-2" data-testid="sales-chart-elastic-types">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="overline">Product Analysis · {data.window?.label || ""}</div>
              <div className="font-display text-lg font-bold text-white">Top Elastic Types (Booked Meters)</div>
            </div>
            <StorefrontIcon size={20} className="text-[var(--accent)]" />
          </div>
          {(data.top_elastic_types || []).length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.top_elastic_types} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid stroke="#27272A" strokeDasharray="2 6" horizontal={false} opacity={0.35} />
                <XAxis type="number" stroke="#71717A" fontSize={11} tickFormatter={(v) => `${(Number(v || 0) / 1e5).toFixed(1)}L`} />
                <YAxis type="category" dataKey="article_type" stroke="#71717A" fontSize={11} width={80} />
                <Tooltip
                  contentStyle={{ background: "#18181B", border: "1px solid #27272A", fontSize: 12 }}
                  formatter={(v) => `${_lakh(v)} L m`}
                />
                <Bar dataKey="meters" fill="var(--accent)" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-zinc-500 py-8 text-sm">No booked lines yet</div>
          )}
        </div>

        {/* ------------- BOTTOM: Top 5 Customers ------------- */}
        <div className="tactical-card p-5" data-testid="sales-top-customers">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="overline">Leaderboard · {data.window?.label || ""}</div>
              <div className="font-display text-lg font-bold text-white">Top 5 Customers</div>
            </div>
            <UsersThreeIcon size={20} className="text-[var(--accent)]" />
          </div>
          <div className="space-y-2">
            {(data.top_customers || []).map((c, i) => {
              const max = (data.top_customers?.[0]?.value_inr) || 1;
              const share = Math.min(100, ((c.value_inr || 0) / max) * 100);
              return (
                <div key={c.customer_id || i} className="p-3 rounded-sm border border-[var(--border)] bg-[var(--bg-2)]/30" data-testid={`sales-top-customer-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-7 w-7 rounded-sm flex items-center justify-center text-[11px] font-mono font-bold ${i === 0 ? "bg-amber-500/20 text-amber-300" : "bg-[var(--bg-2)] text-zinc-400"}`}>#{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{c.customer_name || "—"}</div>
                      <div className="text-[10px] text-zinc-500">{fmtM(c.meters)}m · {c.order_count} order{c.order_count !== 1 ? "s" : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-emerald-400">{fmtInr(c.value_inr)}</div>
                    </div>
                  </div>
                  <div className="mt-2 h-1 bg-[var(--bg-1)] rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--accent)]" style={{ width: `${share}%` }} />
                  </div>
                </div>
              );
            })}
            {(data.top_customers || []).length === 0 && (
              <div className="text-center text-zinc-500 py-6 text-sm">No customers yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
